import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PinpadType } from '../types';
import { TicketType } from '../types';

import { Pinpad } from './pinpad';
import { Ticket } from './ticket';

/** Parsed Paytef receipt payloads stored in `receipts`. */
export type PinpadPayReceipts = {
    merchant: string | null;
    customer: string | null;
};

const _schema = {
    "name": "PINPADPAY",
    "fields": [
        { name: "ticket", type: "objid" },
        { name: "status", type: "string", default: "PE" },
        { name: "reference", type: "string" },
        { name: "orders", type: "string" },
        { name: "amount", type: "number" },
        { name: "pinpad", type: "objid" },
        { name: "terminalcode", type: "string" },
        { name: "operation", type: "string" },
        { name: "authcode", type: "string" },
        { name: "started", type: "date" },
        { name: "finished", type: "date" },
        { name: "signature", type: "boolean", default: "0" },
        { name: "applied", type: "boolean", default: "0" },
        { name: "receipts", type: "string" },
        { name: "kind", type: "string", default: "SA" },
        { name: "origin", type: "string" }
    ],
    relate: [
        { direction: ">", target: "TICKET", by: "ticket", name: "ticket", reason: "ticket", child: false, class: TicketType },
        { direction: ">", target: "PINPAD", by: "pinpad", name: "pinpad", reason: "pinpad", child: false, class: PinpadType },
    ]
} as const;

const _PinpadPayClass = CreateObject(_schema);
if (!_PinpadPayClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class PinpadPay extends _PinpadPayClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
        // New rows are sales until an explicit kind is assigned or synced.
        if (this._data['kind'] == null){
            this._data['kind'] = 'SA';
        }
    }

    Copy(store: Array<DataObject> = []): PinpadPay {
        return this._Copy(store) as PinpadPay;
    }

    /** True while the terminal result is still unknown. */
    get IsPending(): boolean {
        return this.status === 'PE';
    }

    /** True when the terminal approved a different amount than sent. */
    get IsMismatch(): boolean {
        return this.status === 'MM';
    }

    /** True when the terminal approved the charge. */
    get IsApproved(): boolean {
        return this.status === 'AP';
    }

    /** True when the waiter declared the charge as paid manually (no terminal confirmation). */
    get IsManual(): boolean {
        return this.status === 'MA';
    }

    /** True when it is known that no charge completed. */
    get IsDiscarded(): boolean {
        return this.status === 'DE';
    }

    /** True when this row is a card refund attempt (`kind === 'RE'`), not a sale. */
    get IsRefund(): boolean {
        return this.kind === 'RE';
    }

    /** Order numbers covered by this attempt (`orders` JSON), or empty. */
    get Orders(): string[] {
        const _raw = this.orders;
        if (_raw == null || _raw === ''){
            return [];
        }
        try {
            const _parsed = typeof _raw === 'string' ? JSON.parse(_raw) : _raw;
            if (!Array.isArray(_parsed)){
                return [];
            }
            return _parsed.filter((entry): entry is string => typeof entry === 'string');
        }
        catch {
            return [];
        }
    }

    /** Merchant/customer receipt payloads (`receipts` JSON), or nulls. */
    get Receipts(): PinpadPayReceipts {
        const _empty: PinpadPayReceipts = { merchant: null, customer: null };
        const _raw = this.receipts;
        if (_raw == null || _raw === ''){
            return _empty;
        }
        try {
            const _parsed = typeof _raw === 'string' ? JSON.parse(_raw) : _raw;
            if (!_parsed || typeof _parsed !== 'object'){
                return _empty;
            }
            const _obj = _parsed as Record<string, unknown>;
            return {
                merchant: typeof _obj['merchant'] === 'string' ? _obj['merchant'] : null,
                customer: typeof _obj['customer'] === 'string' ? _obj['customer'] : null,
            };
        }
        catch {
            return _empty;
        }
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || relation !== 'ticket' || !(related instanceof Ticket)){
            return;
        }

        if (related === this.ticket){
            related.AddPinpadpay(this);
        }
        else {
            related.DelPinpadpay(this);
        }
    }
}
